import React, { ReactElement, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import BigNumber from 'bignumber.js';

import { DEFAULT_SHORT_DECIMAL_PLACES } from 'modules/common';
import { EpochTimer } from 'modules/common/components/EpochTimer';
import { IStatItemProps, StatItem } from 'modules/common/components/StatItem';
import { Stats as CommonStats } from 'modules/common/components/Stats';
import { useGetStatsQuery } from 'modules/dashboard/actions/getStats';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';

import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

export function Stats(): ReactElement | null {
  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();

  const rawTotalApy = data?.apyTotal;
  const apyTotal = useMemo(
    () =>
      rawTotalApy &&
      new BigNumber(rawTotalApy)
        .decimalPlaces(2, BigNumber.ROUND_HALF_EVEN)
        .toNumber(),
    [rawTotalApy],
  );
  const stakers = data?.stakers;
  const totalRewardsUsd = useMemo(() => {
    if (!data?.totalRewards || !data?.iotaUsdPrice) {
      return null;
    }

    return new BigNumber(data.totalRewards).multipliedBy(data.iotaUsdPrice);
  }, [data]);

  const statsItems: IStatItemProps[] = useMemo(() => {
    return [
      {
        title: t(keys.rewards),
        value: totalRewardsUsd
          ? t(keys.unit.$, {
              value: t(keys.unit.compact, {
                value: totalRewardsUsd
                  .decimalPlaces(DEFAULT_SHORT_DECIMAL_PLACES)
                  .toNumber(),
              }),
            })
          : t(keys.common.none),
      },
      {
        title: t(keys.stakers),
        value: stakers
          ? t(keys.unit.compact, { value: stakers })
          : t(keys.common.none),
      },
      {
        title: t(keys.apy),
        value: apyTotal ? (
          <div>
            <span>{t(keys.unit.percent, { value: apyTotal })}</span>

            <Tooltip
              title={
                <Box component="span" fontSize={12}>
                  {t(keys.apyTooltipText)}
                </Box>
              }
            >
              <Box
                component="span"
                sx={{
                  cursor: 'pointer',
                  fontSize: 18,
                  display: 'inline-block',
                  marginLeft: 1,
                }}
              >
                ⓘ
              </Box>
            </Tooltip>
          </div>
        ) : (
          t(keys.common.none)
        ),
      },
      {
        title: t(keys.epoch),
        value: <EpochTimer />,
      },
    ];
  }, [
    apyTotal,
    keys.apy,
    keys.common.none,
    keys.apyTooltipText,
    keys.epoch,
    keys.rewards,
    keys.stakers,
    keys.unit.compact,
    keys.unit.percent,
    keys.unit.$,
    stakers,
    t,
    totalRewardsUsd,
  ]);

  return (
    <CommonStats>
      {statsItems.map(({ title, value }) => (
        <StatItem key={title} title={title} value={value} />
      ))}
    </CommonStats>
  );
}
